import React, { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { fetchUsers, postUpdateDevice } from "../../../redux/api/admin.api";

import useRoutesHelper from "../../../hooks/useRoutesHelper";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../helpers/apiHelper";

import ContentCard from "../../../components/misc/ContentCard";
import Dropdown, { DropdownValue } from "../../../components/misc/Dropdown";
import CustomButton from "../../../components/CustomButton";
import { useNavigate } from "react-router-dom";
import TextInput from "../../../components/misc/TextInput";
import UploadFileDropzone from "../../../components/misc/UploadFileDropzone";

interface InitialValues {
  userId: number | null;
  observatii: string | null;
  file: File | null;
}

const initialValues: InitialValues = {
  userId: null,
  observatii: null,
  file: null,
};

const schema = Yup.object({
  userId: Yup.number().required("Selectează un utilizator"),
  observatii: Yup.string().nullable(),
  file: Yup.mixed<File>()
    .required("Selectează un fișier")
    .test(
      "fileSize",
      "Fișier prea mare (max 20MB)",
      (v) => !!v && v.size <= 20 * 1024 * 1024,
    ),
});

function AssignUserForDevice() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { routeParams } = useRoutesHelper();
  const { idDevice, model } = routeParams || {};

  const {
    admin: {
      users: {
        data: { data: users = [] } = { data: [] },
        isLoading: isLoadingUsers,
      },
    },
  } = useAppSelector((state) => ({ admin: state.admin }));

  useEffect(() => {
    dispatch(fetchUsers({ page: 1, perPage: 10000 }));
  }, [dispatch]);

  const userOptions: DropdownValue[] = useMemo(
    () =>
      users.map((u: any) => ({
        id: u.userId,
        label:
          [u.prenume, u.nume].filter(Boolean).join(" ") ||
          u.userName ||
          `#${u.userId}`,
      })),
    [users],
  );

  const [isLoading, setIsLoading] = useState(false);

  const handleAssignUser = async (values: InitialValues) => {
    if (!idDevice || !values.userId || !values.file) return;

    setIsLoading(true);
    const result = await dispatch(
      postUpdateDevice({
        idDevice,
        userId: values.userId,
        observatii: values.observatii,
        file: values.file,
      }),
    );

    if (
      checkEndpointStatus(result, postUpdateDevice) !== endpointStatus.pending
    ) {
      setIsLoading(false);
    }
    if (
      checkEndpointStatus(result, postUpdateDevice) === endpointStatus.fulfilled
    ) {
      navigate(-1);
    }
  };

  return (
    <ContentCard
      cardTitle={`Asignează utilizator pentru echipamentul ${model}`}
      isLoading={isLoadingUsers}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleAssignUser}>
        {({
          isValid,
          values,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          errors,
          touched,
        }) => {
          const selectedUser =
            userOptions.find((option) => option.id === values["userId"]) ??
            null;

          return (
            <form onSubmit={handleSubmit} noValidate>
              <div className="flex">
                <Dropdown
                  className="mb-4 mr-2"
                  title="Selectează utilizator"
                  data={userOptions}
                  singleValue={selectedUser}
                  setSingleValue={(value) =>
                    setFieldValue("userId", value?.id ?? null)
                  }
                  error={touched.userId ? (errors.userId as string) : undefined}
                />
              </div>

              <div className="flex">
                <TextInput
                  className="mb-4 mr-2 flex-grow"
                  label="Observații"
                  name="observatii"
                  type="textarea"
                  value={values["observatii"]}
                  onChange={(value) => setFieldValue("observatii", value)}
                />
              </div>

              <UploadFileDropzone
                className="mb-4"
                label="Atașează fișier"
                required
                maxSizeMB={20}
                types={["pdf", "excel", "word"]}
                value={values.file}
                onChange={(f) => setFieldValue("file", f)}
              />

              <div className="flex justify-end">
                <CustomButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting || isLoading}
                  disabled={!isValid || isSubmitting || isLoading}>
                  Salvează
                </CustomButton>
              </div>
            </form>
          );
        }}
      </Formik>
    </ContentCard>
  );
}

export default AssignUserForDevice;
